import { useSubscription } from "@/context/SubscriptionContext";
import { PageHeader } from "@/layouts/PageHeader";
import { PageContainer } from "@/layouts/PageContainer";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { FileText, Sparkles } from "lucide-react";

export default function Usage() {
  const { reportsRemaining, aiCreditsRemaining, seatInfo, loading, error } = useSubscription();

  const statusVariant =
    seatInfo?.status === "active"
      ? "success"
      : seatInfo?.status === "past_due"
      ? "warning"
      : "default";

  const statusLabel =
    seatInfo?.status === "active"
      ? "Active"
      : seatInfo?.status === "past_due"
      ? "Past Due"
      : seatInfo?.status === "cancelled"
      ? "Cancelled"
      : seatInfo?.status ?? "Unknown";

  const reportsUsedPct = seatInfo
    ? Math.min(100, Math.round((seatInfo.reportsUsed / 120) * 100))
    : 0;
  const aiUsedPct = seatInfo
    ? Math.min(100, Math.round((seatInfo.aiGensUsed / 200) * 100))
    : 0;

  return (
    <PageContainer>
      <PageHeader
        title="Usage"
        subtitle="Track your report card and AI generation credits."
      />

      {loading && <p className="text-muted-foreground">Loading...</p>}
      {error && <p className="text-destructive">Failed to load subscription info.</p>}

      {!loading && !error && (
        <div className="space-y-8">

          {/* Wallet balance cards */}
          <div>
            <h2 className="text-[0.8125rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground mb-3">
              Wallet Balance
            </h2>
            <div className="grid grid-cols-2 gap-4 max-[480px]:grid-cols-1">
              <div className="border border-border rounded-xl p-5 bg-card flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <FileText size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">
                    {reportsRemaining ?? "—"}
                  </p>
                  <p className="text-sm text-muted-foreground">Report Credits Remaining</p>
                </div>
              </div>
              <div className="border border-border rounded-xl p-5 bg-card flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Sparkles size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">
                    {aiCreditsRemaining ?? "—"}
                  </p>
                  <p className="text-sm text-muted-foreground">AI Credits Remaining</p>
                </div>
              </div>
            </div>
          </div>

          {/* School seat section */}
          {seatInfo !== null && (
            <div>
              <h2 className="text-[0.8125rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground mb-3">
                School Seat Usage
              </h2>
              <div className="border border-border rounded-xl p-5 bg-card space-y-5">
                <div className="flex items-center gap-2">
                  <Badge variant={statusVariant}>{statusLabel}</Badge>
                  {seatInfo.periodEnd && (
                    <span className="text-xs text-muted-foreground">
                      Period ends{" "}
                      {new Date(seatInfo.periodEnd).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                  )}
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span>Reports Used</span>
                    <span
                      className={
                        seatInfo.reportsUsed >= 120
                          ? "text-destructive font-semibold"
                          : ""
                      }
                    >
                      {seatInfo.reportsUsed} / 120
                    </span>
                  </div>
                  <Progress value={reportsUsedPct} max={100} />
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span>AI Generations Used</span>
                    <span
                      className={
                        seatInfo.aiGensUsed >= 200
                          ? "text-destructive font-semibold"
                          : ""
                      }
                    >
                      {seatInfo.aiGensUsed} / 200
                    </span>
                  </div>
                  <Progress value={aiUsedPct} max={100} />
                </div>
              </div>
            </div>
          )}

        </div>
      )}
    </PageContainer>
  );
}
